import React, { useState, useEffect } from "react";
import { clearSession } from "../shared/_helpers/session";
import TableComponent from "./TableComponent";
import { useSnackbar } from "notistack";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import Loader from "./Loader";
import { createAdvisor } from "../shared/_services/auth.services";
import { updateProfile, blockUnblockUser, getAllUserList, checkBlockedStatus, getCountryCode } from "../shared/_services/common.services";
import { generateValidPassword } from "../shared/_helpers/generatePassword";
import momentTz from "moment-timezone";
import Pagination from "./Pagination";


const AdminAdvisorsList = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [advisorData, setAdvisorData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('add');
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const headers = ["Name", "E-mail", "Phone Number", "No.Clients", "Edit"];
  const [showLoader, setShowLoader] = useState(false);
  const [confirmBlockPopupShow, setConfirmBlockPopupShow] = useState(false);
  const [blockAction, setBlockAction] = useState('');
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);
  const [countrycode, setCountryCodes] = useState([]);


  const confirmBlockPopupClose = () => {
    setConfirmBlockPopupShow(false)
  }

  const openConfirmBlockPopup = (advisor) => {
    setSelectedAdvisor(advisor)
    setBlockAction(advisor.status ? 'block' : 'unblock');
    setConfirmBlockPopupShow(true);
  };


  const getAdvisorsData = async () => {
    setShowLoader(true)
    const response = await getAllUserList("advisor");
    if (response.status === 200) {
      setAdvisorData(response?.data);
    }
    else {
      if (response?.response?.status === 401) {
        clearSession();
        enqueueSnackbar("Your session has been expired.", {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        navigate('/login');
      }
    }
    setShowLoader(false)
  };

  const checkBlocked = async () => {
    const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
    try {
      // Call the API to check the user's blocked status
      const response = await checkBlockedStatus(userId);

      if (response.response ? response?.response?.status == 401 : response.status == 401) {
        clearSession();
        navigate("/login");
      }
      else {
        const { isBlocked, isDeleted } = response.data;

        if (isBlocked || isDeleted) {
          // Clear the currentUser from localStorage
          clearSession();

          // Navigate to the login page
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Error checking blocked status:", error);
      // Optionally handle errors, e.g., show an error message
    }
  }
  const fetchCountryCodes = async () => {
    const code = await getCountryCode();
    setCountryCodes(code?.data);
  };
  useEffect(() => {
    checkBlocked();
    fetchCountryCodes();
    getAdvisorsData();
  }, []); //eslint-disable-line

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, "");
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
    }
    return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .matches(/^[A-Za-z\s]*$/, "Name cannot contain numbers")
      .test("no-leading-space", "Name cannot start with a space", (value) => {
        // Check if value starts with space, return false if it does
        return !value.startsWith(" ");
      }) // Ensure no numbers
      .test("valid-name", "Enter a valid name", (value) => {
        return !!value && value.trim().length > 0; // Ensure at least 1 valid character
      })
      .min(3, "Name must be at least 3 characters")
      .max(30, "Name must be at most 30 characters"),
    email: Yup.string()
      .required("Email is required")
      .test(
        "contains-at-and-dot",
        "Email must contain '@' and '.'",
        (value) => {
          if (!value) return false; // Required validation
          return value.includes("@") && value.includes(".");
        }
      )
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email address"),
    mobile: Yup.string()
      .required("Phone number is required")
      .matches(
        /^(?!.*(\d)\1{2}-(\d)\2{2}-(\d)\3{3})\d{3}-\d{3}-\d{4}$/,
        "Invalid phone number format or repeated digits"
      ),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setShowLoader(true); // Show loader when form submission starts
    const { name, email, mobile, code } = values;
    const [firstName, ...lastNameArray] = name.split(" ");
    const lastName = lastNameArray.join(" ");
    const randomPassword = generateValidPassword();

    let data;
    setShowModal(false);
    try {
      if (modalType === "add") {
        // No `userType`: the server assigns 'advisor' (backend SEC-040).
        data = await createAdvisor({
          firstName,
          lastName,
          email,
          mobile,
          password: randomPassword,
          country_code: code,
          timezone: momentTz.tz.guess(true)
        });
      } else if (modalType === "edit" && selectedAdvisor) {
        const userId = selectedAdvisor._id;
        const updatedProfile = {
          firstName,
          lastName,
          email,
          mobile,
          code,
        };

        data = await updateProfile(userId, updatedProfile);
        
      }

      // `/users/create-advisor` answers 201 and `/users/update` answers 200, so
      // accept any 2xx. `data` is an axios response on success and — because
      // this service layer returns its errors rather than throwing — an axios
      // error otherwise.
      const status = data?.status ?? data?.response?.status;

      if (status >= 200 && status < 300) {
        enqueueSnackbar(
          modalType === "add" ? "Advisor added successfully" : "Advisor updated successfully",
          { variant: "success" }
        );
        // The advisor exists either way; a false here means they never got
        // their credentials and have to be sent them out of band.
        if (modalType === "add" && data?.data?.welcomeEmailSent === false) {
          enqueueSnackbar(
            "Advisor added, but the welcome email could not be sent — send their login details manually.",
            { variant: "warning" }
          );
        }
        setShowModal(false); // Close modal after successful operation
        getAdvisorsData(); // Refresh the advisor list
      } else {
        // Every non-2xx, not only 401. A 400 (duplicate email, failed
        // validation) or 403 (caller is not an admin) previously matched no
        // branch, so the modal closed and nothing was reported at all.
        enqueueSnackbar(
          data?.response?.data?.message ||
          data?.response?.data?.error ||
          `Failed to ${modalType === "add" ? "add" : "update"} advisor`,
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Error during submission:", error);
      enqueueSnackbar(`Failed to ${modalType === "add" ? "add" : "update"} advisor`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false); // Enable the "Save" button
      setShowLoader(false); // Hide loader
    }
  };


  const handleAddAdvisorClick = () => {
    setModalType('add');
    setSelectedAdvisor(null);
    setShowModal(true);
  };

  const handleEditAdvisorClick = (advisor) => {
    setModalType('edit');
    setSelectedAdvisor(advisor);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
  };

  const handleBlock = async () => {
    setConfirmBlockPopupShow(false);
    setShowLoader(true);
    const advisorId = selectedAdvisor._id;
    const newStatus = !selectedAdvisor.status;

    try {
      await blockUnblockUser({
        userId: advisorId,
        status: newStatus
      });
      getAdvisorsData();
      const action = newStatus ? "unblocked" : "blocked";
      enqueueSnackbar(`Advisor ${action} successfully`, { variant: "success" });
    } catch (error) {
      const action = newStatus ? "unblock" : "block";
      enqueueSnackbar(`Failed to ${action} advisor`, { variant: "error" });
    }
    finally {
      setShowLoader(false);
    }
  };

  //pagination code
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;

  const safeAdvisorData = Array.isArray(advisorData) ? advisorData : [];
  const currentRecords = safeAdvisorData.slice(indexOfFirstRecord, indexOfLastRecord);
  const nPages = Math.ceil(safeAdvisorData.length / recordsPerPage);

  return (
    <>
      <div className="page-header d-flex align-items-center justify-content-between">
        <h2>Financial Advisor</h2>
        <button type="button" className="as-btn-primary btn-sharp mt-1 active" onClick={handleAddAdvisorClick}>
          Add New Advisor
        </button>
      </div>
      <div className="p-4">
        {showLoader ? <Loader /> :
          <>
            <TableComponent headers={headers} data={currentRecords} test={"advisor"} onEditAdvisorClick={handleEditAdvisorClick} onBlockAdvisorClick={openConfirmBlockPopup} />
            <Pagination
              nPages={nPages}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </>}
      </div>

      {/* Add or Edit Advisor Modal */}
      <Modal
        size="md"
        className="editProfilePopup"
        show={showModal}
        onHide={closeModal}
        aria-labelledby="modalLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-between">
            <h4 className="mt-1 mb-3">{modalType === "add" ? "Add New Advisor" : "Edit Advisor"}</h4>
            <Link className="close-popup-btn" onClick={closeModal}>
              <i className="fa fa-times"></i>
            </Link>
          </div>
          <Formik
            initialValues={{
              name: selectedAdvisor ? `${selectedAdvisor.firstName} ${selectedAdvisor.lastName}` : "",
              email: selectedAdvisor ? selectedAdvisor.email : "",
              mobile: selectedAdvisor ? selectedAdvisor.mobile : "",
              code: "+1",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting, values, setFieldValue }) => (
              <Form>
                <div className="form-group">
                  <label htmlFor="name">Name</label>
                  <Field
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Full Name"
                    className={`form-control ${errors.name && touched.name ? "is-invalid" : ""}`}
                  />
                  <ErrorMessage name="name" component="div" className="invalid-feedback" />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email address</label>
                  <Field
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Email address"
                    className={`form-control ${errors.email && touched.email ? "is-invalid" : ""}`}
                  />
                  <ErrorMessage name="email" component="div" className="invalid-feedback" />
                </div>
                <div className="form-group input-phone-con">
                  <label htmlFor="mobile">Phone Number</label>
                  <div className="d-flex flex-wrap">
                  <Field as="select" name="code" disabled>
                    {countrycode?.map(item => (
                      <option key={item.code} value={item.code}>
                        ({item.code})
                      </option>
                    ))}
                  </Field>
                  <Field
                    type="text"
                    id="mobile"
                    name="mobile"
                    placeholder="Phone Number"
                    className={`form-control ${errors.mobile && touched.mobile ? "is-invalid" : ""}`}
                    value={formatPhoneNumber(values.mobile)}
                    onChange={e => setFieldValue("mobile", formatPhoneNumber(e.target.value))}
                  />
                  <ErrorMessage name="mobile" component="div" className="invalid-feedback" />
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting} className="as-btn-primary w-100 mt-2">
                  Save
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </Modal>

      {/* block popup Modal*/}
      <Modal
        size="md"
        className="confirmBlockPopup customMiniPopup"
        show={confirmBlockPopupShow}
        onHide={confirmBlockPopupClose}
        aria-labelledby="confirmBlockPopupLabel"
        backdrop="static"
        keyboard={false}
        centered
      >
        <div className="modal-body">
          <div className="d-flex justify-content-between">
            <h4 className="mt-3">  </h4>
            <Link className="close-popup-btn" onClick={confirmBlockPopupClose}>
              <i className="fa fa-times"></i>
            </Link>
          </div>
          <h4 className="mt-3 mb-3 text-center">{`You are attempting to ${blockAction === 'block' ? 'block' : 'unblock'} ${selectedAdvisor ? selectedAdvisor.firstName : ''}.`}</h4>
          <h5 className="text-center f-700">Are you sure?</h5>
          <div className="text-center mt-4 mb-3">
            <button className="as-btn-primary w-75" onClick={handleBlock}>{blockAction === 'block' ? 'Block' : 'Unblock'}</button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AdminAdvisorsList;
